import { ChevronDownIcon, ChevronRightIcon, ListBulletIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import type { PickupLocationCabinet } from '@/services/booking/bookingApi';
import SlotCircle from '@/components/locker/SlotCircle';
import {
  DEFAULT_SLOTS_PER_LIST,
  cabinetListCount,
  hookOnList,
  splitIntoLists,
} from '@/components/locker/cabinetKeyLayout';
import { isKeySlotSelectable } from '@/components/fleet/keySlotSelectable';
import { liveKeyMatchesStored } from '@/utils/liveKeyMatchesStored';
import { KeyStatus, type Key, type VehicleKeyAssignment } from '@/types/locker';

export interface CabinetKeyChoice {
  keyId: string;
  keyName: string;
  cabinetId: string;
  keywinKeyId: string;
}

interface Props {
  cabinets: PickupLocationCabinet[];
  expandedCabinetId: string | null;
  onToggle: (cabinetId: string) => void;
  keysByCabinet: Record<string, Key[]>;
  keysLoadingId: string | null;
  assignments: VehicleKeyAssignment[];
  assignmentsLoaded: boolean;
  vehicleId: string;
  selectedKeyId?: string;
  onSelect: (key: CabinetKeyChoice) => void;
}

export default function ConnectVehicleKeyCabinets({
  cabinets,
  expandedCabinetId,
  onToggle,
  keysByCabinet,
  keysLoadingId,
  assignments,
  assignmentsLoaded,
  vehicleId,
  selectedKeyId,
  onSelect,
}: Props) {
  const { t } = useTranslation();

  return (
    <div className="space-y-2">
      {cabinets.map((cabinet) => {
        const open = expandedCabinetId === cabinet.cabinet_id;
        const cabinetKeys = (keysByCabinet[cabinet.cabinet_id] ?? []).map((key) => {
          const assignment = assignments.find((row) => liveKeyMatchesStored(key.id, key.keyNumber, row.key_id, row.keywin_key_id));
          return {
            ...key,
            vehicleId: assignment?.vehicle_id != null ? String(assignment.vehicle_id) : undefined,
            vehicleRegistration: assignment?.vehicle_registration,
          };
        });
        const lists = splitIntoLists(cabinetKeys, 0, DEFAULT_SLOTS_PER_LIST);
        const listCount = cabinetListCount(0, undefined, cabinetKeys, DEFAULT_SLOTS_PER_LIST);
        const keysReady = keysByCabinet[cabinet.cabinet_id] != null && keysLoadingId !== cabinet.cabinet_id;

        return (
          <div key={cabinet.cabinet_id} className="border border-border rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => onToggle(cabinet.cabinet_id)}
              className="w-full flex items-center gap-2 px-3 py-2 bg-bgBase text-left"
            >
              {open
                ? <ChevronDownIcon className="w-4 h-4 text-textTertiary" />
                : <ChevronRightIcon className="w-4 h-4 text-textTertiary" />}
              <span className="text-sm font-semibold text-textPrimary">
                {cabinet.cabinet_name || cabinet.cabinet_id}
              </span>
            </button>
            {open && (
              <div className="p-2 space-y-3 border-t border-border">
                {!keysReady ? (
                  <div className="animate-pulse h-28 bg-bgElevated rounded-lg" />
                ) : (
                  Array.from({ length: Math.max(listCount, lists.length) }, (_, idx) => {
                    const listKeys = lists[idx] ?? [];
                    const listKeysIn = listKeys.filter((key) => key.status === KeyStatus.InCabinet).length;
                    const listKeysOut = listKeys.filter((key) => key.status === KeyStatus.Taken).length;
                    const listEmpty = DEFAULT_SLOTS_PER_LIST - listKeys.length;
                    return (
                      <div key={idx} className="bg-bgElevated border border-border rounded-lg overflow-hidden">
                        <div className="flex items-center gap-2 px-3 py-2 bg-bgBase border-b border-border">
                          <ListBulletIcon className="w-5 h-5 text-iconColor" />
                          <span className="text-sm font-semibold">List {idx + 1}</span>
                          <span className="text-sm text-textTertiary">
                            {listKeysIn} {t('keyManagement.status.in_cabinet').toLowerCase()}
                            {' · '}
                            {listKeysOut} {t('keyManagement.status.taken').toLowerCase()}
                            {' · '}
                            {listEmpty > 0 ? listEmpty : 0} {t('keyManagement.cabinet.empty').toLowerCase()}
                          </span>
                        </div>
                        <div className="flex flex-nowrap gap-1 overflow-x-auto px-2 py-2">
                          {Array.from({ length: DEFAULT_SLOTS_PER_LIST }, (_, hookIndex) => {
                            const hook = hookIndex + 1;
                            const keyForSlot = listKeys.find((key) => hookOnList(key, DEFAULT_SLOTS_PER_LIST) === hook);
                            const selectable = Boolean(
                              assignmentsLoaded
                              && keyForSlot
                              && isKeySlotSelectable(keyForSlot, vehicleId)
                            );
                            return (
                              <SlotCircle
                                key={`${cabinet.cabinet_id}-${idx}-${hook}`}
                                slotNumber={hook}
                                keyData={keyForSlot}
                                isSelected={keyForSlot != null && selectedKeyId === String(keyForSlot.id)}
                                disabled={!selectable}
                                onClick={() => {
                                  if (!keyForSlot || !selectable || !keyForSlot.keyNumber) return;
                                  onSelect({
                                    keyId: String(keyForSlot.id),
                                    keyName: keyForSlot.name,
                                    cabinetId: cabinet.cabinet_id,
                                    keywinKeyId: keyForSlot.keyNumber,
                                  });
                                }}
                              />
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                )}
                <div className="flex flex-wrap gap-4 px-3 py-2 bg-bgElevated border border-border rounded-lg">
                  <span className="flex items-center gap-1.5 text-sm text-textSecondary">
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500" /> {t('keyManagement.status.in_cabinet')}
                  </span>
                  <span className="flex items-center gap-1.5 text-sm text-textSecondary">
                    <span className="w-2.5 h-2.5 rounded-full bg-orange-500" /> {t('keyManagement.status.taken')}
                  </span>
                  <span className="flex items-center gap-1.5 text-sm text-textSecondary">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500" /> {t('keyManagement.status.lost')}
                  </span>
                  <span className="flex items-center gap-1.5 text-sm text-textSecondary">
                    <span className="w-2.5 h-2.5 rounded-full bg-gray-300 dark:bg-gray-600" /> {t('keyManagement.cabinet.emptySlot')}
                  </span>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
